// src/components/templates/OrganisationTemplate.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Eye, Shield, ArrowLeft, LogIn, X, Building2 } from 'lucide-react';
import { organisationService } from '../../services/organisationService';
// Import all renderers
import { HeroSectionRenderer } from '../renderers/HeroSectionRenderer';
import { AboutSectionRenderer } from '../renderers/AboutSectionRenderer';
import { LeadershipSectionRenderer } from '../renderers/LeadershipSectionRenderer';
import { ServicesSectionRenderer } from '../renderers/ServicesSectionRenderer';
import { EventsSectionRenderer } from '../renderers/EventsSectionRenderer';
import { NewsSectionRenderer } from '../renderers/NewsSectionRenderer';
import { ContactSectionRenderer } from '../renderers/ContactSectionRenderer';

import { BookingWidget } from './organisation/BookingWidget';
import { OrganisationPortal } from './organisation/OrganisationPortal';
import { organizationAccessService } from '../../services/organizationAccessService';

export const OrganisationTemplate = ({ 
  bookData, 
  bookId, 
  formData, 
  user, 
  onUpdate, 
  isPreview = false,
  customStyling = {},
  onInteraction 
}) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(() => {
    // Get view access settings
    const viewAccess = bookData?.viewAccess || {
      enablePublicView: true,
      enableManagementView: true,
      allowViewToggle: true,
      defaultView: 'public',
      requireAuthForManagement: true
    };

    // Determine initial view based on settings
    if (!viewAccess.enablePublicView && viewAccess.enableManagementView) {
      return 'management'; // Management-only mode
    }
    if (viewAccess.enablePublicView && !viewAccess.enableManagementView) {
      return 'public'; // Public-only mode
    }
    return viewAccess.defaultView || 'public'; // Default behavior
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);

  const viewAccess = bookData?.viewAccess || {
    enablePublicView: true,
    enableManagementView: true,
    allowViewToggle: true,
    defaultView: 'public',
    requireAuthForManagement: true
  };

  const isCurrentViewAllowed = () => {
    if (viewMode === 'public') return viewAccess.enablePublicView;
    if (viewMode === 'management') return viewAccess.enableManagementView && isAuthenticated;
    return false;
  };

  // Override view mode if not allowed
  useEffect(() => {
    if (!isCurrentViewAllowed()) {
      if (viewAccess.enablePublicView) {
        setViewMode('public');
      } else if (viewAccess.enableManagementView && isAuthenticated) {
        setViewMode('management');
      }
    }
  }, [isAuthenticated, viewAccess]);

  useEffect(() => {
    checkAuthentication();
  }, [user]);

  const checkAuthentication = async () => {
    if (!user) {
      setIsAuthenticated(false);
      return;
    }

    // Basic owner/admin access (keep existing logic)
    if (user?.role === 'admin' || 
        user?.permissions?.includes('manage_organisation') ||
        user?.uid === bookData?.ownerId) {
      setIsAuthenticated(true);
      setIsOwner(bookData.ownerId === user?.uid);
      return;
    }

    // Enhanced organization-specific access checking
    try {
      const accessLevel = formData?.managementAccessLevel || 'internal';
      
      // For 'internal' level, need basic member status
      if (accessLevel === 'internal') {
        const isMember = await organizationAccessService.checkMembership(bookId, user.uid);
        setIsAuthenticated(isMember);
      }
      // For 'members' level, need confirmed member status  
      else if (accessLevel === 'members') {
        const memberStatus = await organizationAccessService.getMemberStatus(bookId, user.uid);
        setIsAuthenticated(memberStatus?.status === 'active' && memberStatus?.role !== 'public');
      }
      // For 'restricted' level, need staff+ privileges
      else if (accessLevel === 'restricted') {
        const memberStatus = await organizationAccessService.getMemberStatus(bookId, user.uid);
        setIsAuthenticated(memberStatus?.role === 'staff' || memberStatus?.role === 'manager' || memberStatus?.role === 'admin');
      }
      else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error checking organization access:', error);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    const loadSections = async () => {
      if (!bookId) return;
      
      try {
        const sectionsData = await organisationService.getOrganisationSections(bookId);
        setSections(sectionsData);
      } catch (error) {
        console.error('Error loading organisation sections:', error);
        setSections([]);
      } finally {
        setLoading(false);
      }
    };

    loadSections();
  }, [bookId]);

  // Get enabled sections from book settings
  const enabledSections = bookData?.enableSections || {};
  
  // Section order definition
  const sectionOrder = ['hero', 'about', 'leadership', 'services', 'events', 'news', 'contact'];
  
  // Filter and sort visible sections
  const visibleSections = sections
    .filter(section => {
      // Check if section type is enabled
      return enabledSections[section.type] === true && section.enabled !== false;
    })
    .sort((a, b) => {
      const aIndex = sectionOrder.indexOf(a.type);
      const bIndex = sectionOrder.indexOf(b.type);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });

  const handleViewModeToggle = async () => {
    // Get view access settings
    const viewAccess = bookData?.viewAccess || {
      enablePublicView: true,
      enableManagementView: true,
      allowViewToggle: true,
      defaultView: 'public',
      requireAuthForManagement: true
    };

    // Check if toggle is allowed
    if (!viewAccess.allowViewToggle) {
      console.log('View toggle is disabled in settings');
      return;
    }

    // Switching back to public view
    if (viewMode === 'management') {
      // Check if public view is enabled
      if (!viewAccess.enablePublicView) {
        console.log('Public view is disabled');
        return;
      }
      setViewMode('public');
      return;
    }

    // Switching to management mode - check if it's enabled
    if (!viewAccess.enableManagementView) {
      console.log('Management view is disabled');
      return;
    }

    // Check authentication requirement
    if (viewAccess.requireAuthForManagement && !user) {
      // Redirect to login with return URL
      const currentPath = window.location.pathname;
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}&action=management`;
      return;
    }

    // Check if user has access
    if (isAuthenticated) {
      setViewMode('management');
    } else {
      // Show appropriate auth modal based on access level requirements
      const accessLevel = formData?.managementAccessLevel || 'internal';
      
      try {
        const accessResult = await organizationAccessService.checkOrganizationAccess(bookId, user.uid);
        
        if (accessResult.hasAccess) {
          setIsAuthenticated(true);
          setViewMode('management');
        } else {
          // User needs to request access
          setShowAuthModal(true);
        }
      } catch (error) {
        console.error('Access check failed:', error);
        setShowAuthModal(true);
      }
    }
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
    await checkAuthentication(); // Re-check access
    if (isAuthenticated) {
      setViewMode('management');
    }
  };

  const getSectionComponent = (section, index) => {
    const sectionProps = {
      data: section,
      bookData,
      customStyling,
      onInteraction,
      index
    };

    switch (section.type) {
      case 'hero':
        return <HeroSectionRenderer key={section.id} {...sectionProps} />;
      case 'about':
        return <AboutSectionRenderer key={section.id} {...sectionProps} />;
      case 'leadership':
        return <LeadershipSectionRenderer key={section.id} {...sectionProps} />;
      case 'services':
        return <ServicesSectionRenderer key={section.id} {...sectionProps} />;
      case 'events':
        return <EventsSectionRenderer key={section.id} {...sectionProps} />;
      case 'news':
        return <NewsSectionRenderer key={section.id} {...sectionProps} />;
      case 'contact':
        return <ContactSectionRenderer key={section.id} {...sectionProps} />;
      default:
        console.warn(`Unknown section type: ${section.type}`);
        return null;
    }
  };

  const renderViewToggle = () => {
    if (isPreview) return null;
    
    // Don't show toggle if only one view is enabled
    const canToggle = viewAccess.allowViewToggle && 
                     viewAccess.enablePublicView && 
                     viewAccess.enableManagementView;
    
    if (!canToggle) return null;
    
    return (
      <div className="fixed bottom-6 right-6 z-40">
        <div className="bg-white rounded-full shadow-lg border border-gray-200 overflow-hidden">
          <button
            onClick={handleViewModeToggle}
            className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors group"
          >
            {viewMode === 'public' ? (
              <>
                <Settings className="h-5 w-5 text-gray-600 group-hover:text-blue-600" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">
                  Management
                </span>
                {viewAccess.requireAuthForManagement && !isAuthenticated && (
                  <Shield className="h-4 w-4 text-blue-600" />
                )}
              </>
            ) : (
              <>
                <Eye className="h-5 w-5 text-gray-600 group-hover:text-green-600" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-green-700">
                  Public View
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderAccessDenied = () => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Management Access Required</h2>
        <p className="text-gray-600 mb-6">
          This organisation is in management-only mode. Please sign in to access.
        </p>
        <button
          onClick={() => {
            const currentPath = window.location.pathname;
            window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}&action=management`;
          }}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
        >
          <LogIn className="h-5 w-5" />
          Sign In
        </button>
      </div>
    </div>
  );

  // Helper to determine what to render
  const getViewContent = () => {
    // Management-only mode
    if (!viewAccess.enablePublicView && viewAccess.enableManagementView) {
      if (isAuthenticated && viewMode === 'management') {
        return (
          <OrganisationPortal
            bookData={bookData}
            bookId={bookId}
            formData={formData}
            user={user}
            onUpdate={onUpdate}
          />
        );
      }
      return renderAccessDenied();
    }
    
    // Public-only mode
    if (viewAccess.enablePublicView && !viewAccess.enableManagementView) {
      return renderPublicContent();
    }
    
    // Both views enabled (standard behavior)
    return viewMode === 'public' ? renderPublicContent() : (
      <OrganisationPortal
        bookData={bookData}
        bookId={bookId}
        formData={formData}
        user={user}
        onUpdate={onUpdate}
        onSwitchToPublic={() => viewAccess.enablePublicView && setViewMode('public')}
      />
    );
  };

  const Header = ({ isOwner, bookData, customStyling, navigate }) => {
      const [isSquare, setIsSquare] = useState(true);
  
      const handleImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.target;
        setIsSquare(naturalWidth === naturalHeight);
      };
  
      return (
        <div className="flex items-center gap-2">
          {/* Return Arrow for Album/Book Owner */}
          {isOwner && (
            <button
              onClick={() => navigate("/dashboard")}
              className="p-0 hover:bg-gray-100 rounded-lg transition-colors"
              title="Return to Dashboard"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
          )}
  
          {/* Logo Display */}
          {bookData?.logoUrl && (
            <div className="flex-shrink-0">
              <img
                src={bookData.logoUrl}
                alt={`${bookData.title || bookData.botName} logo`}
                className="h-16 w-auto object-contain rounded-lg"
                onLoad={handleImageLoad}
              />
            </div>
          )}
  
          {/* Title Section (only show if logo is square OR no logo at all) */}
          {(isSquare || !bookData?.logoUrl) && (
            <div className="min-w-0">
              <h1
                className="text-2xl font-bold text-gray-900 truncate"
                style={{ color: customStyling.primaryColor }}
              >
                {bookData.title || bookData.botName}
              </h1>
              {bookData.description && (
                <p className="text-gray-600 mt-1 text-sm line-clamp-2">
                  {bookData.description}
                </p>
              )}
            </div>
          )}
        </div>
      );
    }

  const renderPublicContent = () => (
    <div className="bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Header 
                isOwner={isOwner} 
                bookData={bookData} 
                customStyling={customStyling} 
                navigate={navigate} 
              />

            {/* Quick Contact */}
            <div className="hidden md:flex items-center gap-4 text-sm">
              {bookData?.contactSection?.phone && (
                <a 
                  href={`tel:${bookData.contactSection.phone}`}
                  className="text-gray-600 hover:text-blue-600"
                >
                  {bookData.contactSection.phone}
                </a>
              )}
              {bookData?.contactSection?.email && (
                <a 
                  href={`mailto:${bookData.contactSection.email}`}
                  className="text-gray-600 hover:text-blue-600"
                >
                  {bookData.contactSection.email}
                </a>
              )}
            </div>
          </div>
        </div>
      </nav>

      {visibleSections.map((section, index) => (
        <div 
          key={section.id} 
          id={`section-${section.type}`}
          className="section-scroll-target"
        >
          {getSectionComponent(section, index)}
        </div>
      ))}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                {bookData?.logoUrl ? (
                  <img 
                    src={bookData.logoUrl} 
                    alt="Logo"
                    className="h-8 w-8 object-cover rounded"
                  />
                ) : (
                  <Building2 className="h-8 w-8 text-blue-400" />
                )}
                <span className="font-bold text-lg">{bookData?.botName}</span>
              </div>
              {formData?.description && (
                <p className="text-gray-400 mb-4">{bookData.description}</p>
              )}
            </div>

            <div>
              <h3 className="font-semibold mb-4">Contact Info</h3>
              <div className="space-y-2 text-gray-400">
                {bookData?.contactSection?.address && <p>{bookData.contactSection.address}</p>}
                {bookData?.contactSection?.phone && (
                  <p>
                    <a href={`tel:${bookData.contactSection.phone}`} className="hover:text-white">
                      {bookData.contactSection.phone}
                    </a>
                  </p>
                )}
                {bookData?.contactSection?.email && (
                  <p>
                    <a href={`mailto:${bookData.contactSection.email}`} className="hover:text-white">
                      {bookData.contactSection.email}
                    </a>
                  </p>
                )}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <div className="space-y-2">
                {bookData?.enabledSections?.about && (
                  <a href="#about" className="block text-gray-400 hover:text-white">About Us</a>
                )}
                {bookData?.enabledSections?.services && (
                  <a href="#services" className="block text-gray-400 hover:text-white">Services</a>
                )}
                {bookData?.enabledSections?.contact && (
                  <a href="#contact" className="block text-gray-400 hover:text-white">Contact</a>
                )}
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} {bookData?.botName}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );

  const renderAuthModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-semibold">Management Access</h3>
          <button
            onClick={() => setShowAuthModal(false)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Access Management Portal
            </h4>
            <p className="text-gray-600">
              Sign in to access the organization management platform
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleAuthSuccess}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium"
            >
              <LogIn className="h-5 w-5" />
              Sign In to Management
            </button>
            
            <button
              onClick={() => setShowAuthModal(false)}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 font-medium"
            >
              Cancel
            </button>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Demo Access:</strong>
            </p>
            <p className="text-xs text-gray-500">
              In production, this would integrate with your authentication system. 
              For demo purposes, clicking "Sign In" will grant access.
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50" style={{customStyling}}>
      {/* Render the appropriate view */}
      {viewMode === 'public' ? renderPublicContent() : (
        <OrganisationPortal
          bookData={bookData}
          bookId={bookId}
          formData={formData}
          user={user}
          onUpdate={onUpdate}
          onSwitchToPublic={() => setViewMode('public')}
        />
      )}

      {bookData?.enableBooking && (
          <>
            {/* Inline booking section (if no dedicated booking section exists) */}
            {!visibleSections.find(s => s.type === 'booking') && (
              <section className="py-16 px-4 bg-gray-50">
                <div className="max-w-4xl mx-auto">
                  <BookingWidget
                    bookData={bookData}
                    bookId={bookId}
                    isFloating={false}
                    onInteraction={onInteraction}
                  />
                </div>
              </section>
            )}
            
            {/* Floating booking button */}
            <BookingWidget
              bookData={bookData}
              bookId={bookId}
              isFloating={true}
              onInteraction={onInteraction}
            />
          </>
        )}

      {/* View Toggle Button */}
      {renderViewToggle()}
    </div>
  );
};